import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, 
  Button, Container, Chip, CircularProgress, Grid, TableContainer, Divider, Stack,
  Stepper, Step, StepLabel, StepConnector, stepConnectorClasses,
  Menu, MenuItem, ListItemIcon, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  IconButton
} from "@mui/material";
import { styled } from '@mui/material/styles';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import InventoryIcon from "@mui/icons-material/Inventory";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import DraftsIcon from '@mui/icons-material/Drafts';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { alpha } from "@mui/material/styles";
import { getPurchaseInvoiceById, updatePurchaseDocumentStatus } from "../services/purchaseService";
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useStoreInfo } from '../../../hooks/useCompanyInfo';
import DescriptionIcon from '@mui/icons-material/Description';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';

// ✅ Custom Stepper Connector with dynamic colors
const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #667eea 0%, #764ba2 100%)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #4caf50 0%, #388e3c 100%)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderRadius: 1,
  },
}));

// ✅ Custom Step Icon
const ColorlibStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...(ownerState.active && {
    backgroundImage: 'linear-gradient(136deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  }),
  ...(ownerState.completed && {
    backgroundImage: 'linear-gradient(136deg, #4caf50 0%, #388e3c 100%)',
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className, icon } = props;

  const icons = {
    1: <DraftsIcon />,
    2: <LocalShippingIcon />,
    3: <FactCheckIcon />,
    4: <CheckCircleIcon />,
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {icons[String(icon)]}
    </ColorlibStepIconRoot>
  );
}

// ✅ Document Status Steps
const DOCUMENT_STATUS_STEPS = ['Draft', 'Received', 'VerifyQuantity', 'Posted'];

// ✅ Get status step index
const getStatusStep = (status) => {
  const statusMap = {
    'Draft': 0,
    'Received': 1,
    'VerifyQuantity': 2,
    'Posted': 3,
  };
  return statusMap[status] || 0;
};

// ✅ Get available status transitions
const getAvailableStatuses = (currentStatus) => {
  const statuses = [];

  if (currentStatus === 'Draft') {
    statuses.push({ value: 'Received', label: 'Mark as Received', icon: <LocalShippingIcon />, color: '#2196f3' });
    statuses.push({ value: 'Posted', label: 'Post Directly', icon: <CheckCircleIcon />, color: '#4caf50' });
  }

  if (currentStatus === 'Received') {
    statuses.push({ value: 'VerifyQuantity', label: 'Verify Quantity', icon: <FactCheckIcon />, color: '#ff9800' });
    statuses.push({ value: 'Draft', label: 'Back to Draft', icon: <DraftsIcon />, color: '#9e9e9e' });
    // ❌ Cannot go directly from Received to Posted - must verify quantity first
  }

  if (currentStatus === 'VerifyQuantity') {
    statuses.push({ value: 'Posted', label: 'Post', icon: <CheckCircleIcon />, color: '#4caf50' });
    statuses.push({ value: 'Received', label: 'Back to Received', icon: <LocalShippingIcon />, color: '#2196f3' });
  }

  if (currentStatus === 'Posted') {
    // ✅ Posted is final - no transitions, no edits, no deletions allowed
  }

  return statuses;
};

function PurchaseOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [deliveryPreview, setDeliveryPreview] = useState({ open: false, html: '' });
  const deliveryIframeRef = useRef(null);
  const { t } = useTranslation();
  const language = useSelector((state) => state.settings?.language) || 'en';
  const { storeInfo } = useStoreInfo();
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });

  const loadPurchaseInvoice = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getPurchaseInvoiceById(id);
      setInvoice(data.payload || data);
    } catch (error) {

      toast.error('Failed to load purchase invoice');
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadPurchaseInvoice();
  }, [loadPurchaseInvoice]);

  // ✅ Handle document status update with confirmation
  const handlePrintDeliveryOrder = async () => {
    try {
      // Single EN template; all labels injected via t() so they follow the app language
      const res = await fetch('/deliverynotetemplates/deliverynote-template-en.html');
      let html = await res.text();

      const fmt = (dateStr) => {
        if (!dateStr) return '';
        const [year, month, day] = dateStr.slice(0, 10).split('-');
        return `${day}/${month}/${year}`;
      };

      const lineItems = invoice.LineItems || [];
      const productRows = lineItems.map((item, i) => `
        <tr class="${i % 2 === 1 ? 'alternate-row' : ''}">
          <td>${i + 1}</td>
          <td>${item.ProductName || item.Product?.Name || ''}</td>
          <td style="text-align:center;">${item.Quantity || 0}</td>
        </tr>
      `).join('');
      const totalQty = lineItems.reduce((s, item) => s + Number(item.Quantity || 0), 0);

      html = html
        // Translated text labels
        .replace(/\{\{DocTitle\}\}/g, t('delivery_receipt') || 'Delivery Receipt')
        .replace(/\{\{DateLabel\}\}/g, t('date') || 'Date')
        .replace(/\{\{FromLabel\}\}/g, t('from') || 'From')
        .replace(/\{\{ToLabel\}\}/g, t('deliver_to') || 'Deliver To')
        .replace(/\{\{ProductHeader\}\}/g, t('product') || 'Product')
        .replace(/\{\{QuantityHeader\}\}/g, t('quantity') || 'Quantity')
        .replace(/\{\{TotalLabel\}\}/g, t('total') || 'Total')
        .replace(/\{\{FooterText\}\}/g, t('thank_you_for_business') || 'Thank you for your business!')
        // Data fields
        .replace(/\{\{DeliveryNoteDate\}\}/g, fmt(invoice.InvoiceDate))
        .replace(/\{\{DeliveryNoteNumber\}\}/g, `${invoice.PurchaseInvoiceNumber}`)
        .replace(/\{\{CompanyName\}\}/g, invoice.Supplier?.Name || '')
        .replace(/\{\{CustomerName\}\}/g, storeInfo?.name || '')
        .replace(/\{\{\{ProductRows\}\}\}/g, productRows)
        .replace(/\{\{TotalQuantity\}\}/g, String(totalQty))
        .replace(/\{\{CustomTextSection\}\}/g, '');

      setDeliveryPreview({ open: true, html });
    } catch {
      toast.error('Failed to load delivery note template');
    }
  };

  // ✅ Handle document status update with confirmation
  const handleStatusUpdate = async (newStatus) => {
    setConfirmDialog({
      open: true,
      title: `Update to ${newStatus}?`,
      message: `Are you sure you want to change the document status to "${newStatus}"? This action will update the purchase order workflow.`,
      onConfirm: async () => {
        try {
          await updatePurchaseDocumentStatus({
            PurchaseInvoiceId: parseInt(id),
            DocumentStatus: newStatus,
          });
          toast.success(`Document status updated to ${newStatus}`);
          await loadPurchaseInvoice();
        } catch (error) {

          toast.error(error?.response?.data?.message || 'Failed to update status');
        } finally {
          handleCloseConfirmDialog();
          handleCloseStatusMenu();
        }
      }
    });
  };

  // ✅ Menu handlers
  const handleOpenStatusMenu = (event) => {
    setStatusMenuAnchor(event.currentTarget);
  };

  const handleCloseStatusMenu = () => {
    setStatusMenuAnchor(null);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      title: '',
      message: '',
      onConfirm: null
    });
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: '#667eea', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading purchase order details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!invoice) {
    return (
      <Container maxWidth="lg" sx={{ pt: 8 }}>
        <Paper elevation={3} sx={{ p: 6, borderRadius: 3, textAlign: "center", bgcolor: '#fff5f5' }}>
          <Typography variant="h5" color="error" mb={2} fontWeight="bold">
            ⚠️ Purchase Invoice Not Found
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            The requested purchase invoice could not be found in the system.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate("/purchases")}
            sx={{ borderRadius: 2, px: 4 }}
          >
            Back to Purchase Orders
          </Button>
        </Paper>
      </Container>
    );
  }

  const subTotal = invoice.SubTotal || 0;
  const totalTax = invoice.TaxAmount || 0;
  const totalAmount = invoice.TotalAmount || 0;
  const discountAmount = invoice.DiscountAmount || 0;
  const documentStatus = invoice.DocumentStatus || 'Draft';
  const statusStep = getStatusStep(documentStatus);
  const availableStatuses = getAvailableStatuses(documentStatus);

  // Format dates to DD/MM/YYYY
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    // ✅ Extract date parts directly from ISO string to avoid timezone issues
    const datePart = dateStr.slice(0, 10); // "2026-07-15"
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DescriptionIcon sx={{ fontSize: 40, color: '#667eea', mr: 2 }} />
            <Box>
              <Typography variant="h4" fontWeight="700" sx={{ color: '#667eea' }}>
                Purchase Order #{invoice.PurchaseInvoiceNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Invoice Date: {formatDate(invoice.InvoiceDate)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/purchases')}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              Back
            </Button>

            {availableStatuses.length > 0 && (
              <Button
                variant="contained"
                endIcon={<KeyboardArrowDownIcon />}
                onClick={handleOpenStatusMenu}
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                  boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
                }}
              >
                Change Status
              </Button>
            )}

            {/* ✅ Only allow editing in Draft status */}
            {documentStatus === 'Draft' && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/purchases/add?id=${id}`)}
                sx={{ 
                  borderRadius: 2,
                  bgcolor: '#f59e0b',
                  '&:hover': { bgcolor: '#d97706' }
                }}
              >
                Continue Draft
              </Button>
            )}

            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrintDeliveryOrder}
              sx={{ borderRadius: 2, borderColor: '#667eea', color: '#667eea', '&:hover': { bgcolor: alpha('#667eea', 0.05) } }}
            >
              {t('delivery_receipt') || 'Delivery Receipt'}
            </Button>
          </Box>
        </Box>

        {/* Status Progress Stepper */}
        <Box sx={{ mt: 4 }}>
          <Stepper 
            alternativeLabel 
            activeStep={statusStep} 
            connector={<ColorlibConnector />}
          >
            {DOCUMENT_STATUS_STEPS.map((label) => (
              <Step key={label}>
                <StepLabel StepIconComponent={ColorlibStepIcon}>
                  <Typography variant="body2" fontWeight={600}>
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Paper>

      {/* Status Change Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleCloseStatusMenu}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 200,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          }
        }}
      >
        {availableStatuses.map((status) => (
          <MenuItem
            key={status.value}
            onClick={() => handleStatusUpdate(status.value)}
            disabled={status.disabled}
            sx={{
              py: 1.5,
              '&:hover': {
                bgcolor: alpha(status.color, 0.1),
              }
            }}
          >
            <ListItemIcon sx={{ color: status.color }}>
              {status.icon}
            </ListItemIcon>
            <ListItemText 
              primary={status.label}
              primaryTypographyProps={{
                fontWeight: 600,
                color: status.disabled ? 'text.disabled' : status.color
              }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Delivery Order Preview Dialog */}
      <Dialog
        open={deliveryPreview.open}
        onClose={() => setDeliveryPreview({ open: false, html: '' })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#667eea', borderBottom: '1px solid', borderColor: 'divider', pr: 6 }}>
          {t('delivery_receipt') || 'Delivery Receipt'}
          <IconButton
            onClick={() => setDeliveryPreview({ open: false, html: '' })}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <iframe
            ref={deliveryIframeRef}
            srcDoc={deliveryPreview.html}
            title="Delivery Order Preview"
            style={{ width: '100%', height: '640px', border: 'none', display: 'block' }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => deliveryIframeRef.current?.contentWindow?.print()}
            sx={{ background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)', borderRadius: 2 }}
          >
            Print
          </Button>
          <Button variant="outlined" onClick={() => setDeliveryPreview({ open: false, html: '' })} sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 400,
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 700, 
          color: '#667eea',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2
        }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText sx={{ fontSize: '1rem', color: 'text.primary' }}>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button 
            onClick={handleCloseConfirmDialog}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDialog.onConfirm}
            variant="contained"
            color="primary"
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
            }}
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Content */}
      <Box>

        <Grid container spacing={3}>
          {/* Supplier Information */}
          <Grid item xs={12} md={6}>
            <Paper elevation={5} sx={{ p: 3, borderRadius: 3, border: '3px solid #667eea', height: '100%', boxShadow: '0 6px 24px rgba(102, 126, 234, 0.12)' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                p: 2,
                bgcolor: '#e0e7ff',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.1)'
              }}>
                <Box sx={{ bgcolor: '#667eea', p: 1.5, borderRadius: 2, mr: 2, boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)' }}>
                  <PersonIcon sx={{ color: 'white', fontSize: 32 }} />
                </Box>
                <Typography variant="h5" fontWeight="bold" color="#667eea">
                  Supplier Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Supplier Name
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="text.primary">
                    {invoice.Supplier?.Name || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Contact Person
                  </Typography>
                  <Typography variant="body1" color="text.primary">
                    {invoice.Supplier?.Contactperson || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Phone Number
                  </Typography>
                  <Typography variant="body1" color="text.primary">
                    {invoice.Supplier?.PhoneNumber || invoice.Supplier?.MobileNumber || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Email
                  </Typography>
                  <Typography variant="body1" color="text.primary">
                    {invoice.Supplier?.Email || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    BTW Number
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="primary">
                    {invoice.Supplier?.BTWNumber || 'N/A'}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Invoice Information */}
          <Grid item xs={12} md={6}>
            <Paper elevation={5} sx={{ p: 3, borderRadius: 3, border: '3px solid #667eea', height: '100%', boxShadow: '0 6px 24px rgba(102, 126, 234, 0.12)' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                p: 2,
                bgcolor: '#e0e7ff',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.1)'
              }}>
                <Box sx={{ bgcolor: '#667eea', p: 1.5, borderRadius: 2, mr: 2, boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)' }}>
                  <ReceiptIcon sx={{ color: 'white', fontSize: 32 }} />
                </Box>
                <Typography variant="h5" fontWeight="bold" color="#667eea">
                  Invoice Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Invoice Number
                  </Typography>
                  <Typography variant="body1" fontWeight={700} color="primary">
                    {invoice.PurchaseInvoiceNumber || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Reference Number
                  </Typography>
                  <Typography variant="body1" color="text.primary">
                    {invoice.ReferenceNumber || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Invoice Date
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="text.primary">
                    {formatDate(invoice.InvoiceDate)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Due Date
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="text.primary">
                    {formatDate(invoice.DueDate)}
                  </Typography>
                </Box>
                {/* Status Badges Section - Enhanced */}
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, mb: 1.5, display: 'block' }}>
                    Status Information
                  </Typography>
                  <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ gap: 1.5 }}>
                    <Box sx={{ 
                      border: '2px solid', 
                      borderColor: statusStep === 3 ? '#4caf50' : statusStep === 2 ? '#ff9800' : statusStep === 1 ? '#2196f3' : '#9e9e9e',
                      borderRadius: 2,
                      p: 1.5,
                      bgcolor: alpha(statusStep === 3 ? '#4caf50' : statusStep === 2 ? '#ff9800' : statusStep === 1 ? '#2196f3' : '#9e9e9e', 0.1),
                      flex: '1 1 auto',
                      minWidth: '140px'
                    }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                        Document Status
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ 
                        color: statusStep === 3 ? '#4caf50' : statusStep === 2 ? '#ff9800' : statusStep === 1 ? '#2196f3' : '#9e9e9e',
                        mt: 0.5
                      }}>
                        {documentStatus}
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      border: '2px solid', 
                      borderColor: invoice.Status === 'Paid' ? '#10b981' : invoice.Status === 'Partial Paid' ? '#f59e0b' : '#ef4444',
                      borderRadius: 2,
                      p: 1.5,
                      bgcolor: alpha(invoice.Status === 'Paid' ? '#10b981' : invoice.Status === 'Partial Paid' ? '#f59e0b' : '#ef4444', 0.1),
                      flex: '1 1 auto',
                      minWidth: '140px'
                    }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                        Payment Status
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ 
                        color: invoice.Status === 'Paid' ? '#10b981' : invoice.Status === 'Partial Paid' ? '#f59e0b' : '#ef4444',
                        mt: 0.5
                      }}>
                        {invoice.Status || 'Unknown'}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
                {invoice.Status === 'Paid' && invoice.PaidDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                      Payment Date
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="success.main">
                      {formatDate(invoice.PaidDate)}
                    </Typography>
                  </Box>
                )}
                {invoice.Status === 'Paid' && invoice.PaymentMethod && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                      Payment Method
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="text.primary">
                      {invoice.PaymentMethod}
                    </Typography>
                  </Box>
                )}
                
                {/* Invoice Total */}
                <Divider sx={{ my: 2 }} />
                <Box sx={{ bgcolor: alpha('#667eea', 0.1), p: 2, borderRadius: 2, mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    Invoice Total
                  </Typography>
                  <Typography variant="h5" fontWeight={800} color="#667eea" sx={{ mt: 0.5 }}>
                    €{totalAmount.toFixed(2)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Line Items - Full Width */}
          <Grid item xs={12} sx={{ mt: 6 }}>
            <Paper elevation={6} sx={{ p: 3, borderRadius: 3, border: '3px solid #3b82f6', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.15)' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                p: 2,
               
                bgcolor: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)'
              }}>
                <Box sx={{ bgcolor: '#3b82f6', p: 1.5, borderRadius: 2, mr: 2, boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)' }}>
                  <InventoryIcon sx={{ color: 'white', fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight="bold" color="#3b82f6">
                    Line Items
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    {invoice.LineItems?.length || 0} item(s)
                  </Typography>
                </Box>
              </Box>
              
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.75rem' }}>#</TableCell>
                      <TableCell sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.75rem' }}>Product</TableCell>
                      <TableCell sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.75rem' }}>SKU</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.75rem' }}>Qty</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.75rem' }}>Unit Cost</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.75rem' }}>Tax %</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#f9fafb', fontWeight: 700, fontSize: '0.75rem' }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.LineItems && invoice.LineItems.length > 0 ? (
                      invoice.LineItems.map((item, idx) => (
                        <TableRow key={item.Id || idx} hover sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                          <TableCell sx={{ fontSize: '0.85rem' }}>{idx + 1}</TableCell>
                          <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{item.ProductName || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{item.ProductSKU || 'N/A'}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.85rem' }}>{item.Quantity || 0}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.85rem' }}>€{(item.UnitCost || 0).toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.85rem' }}>{item.TaxPercentage || 0}%</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#059669' }}>
                            €{(item.LineTotal || 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary" fontSize="0.9rem">No items found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Totals Section */}
              {invoice.LineItems && invoice.LineItems.length > 0 && (
                <Box sx={{ mt: 3, pt: 2, borderTop: '2px solid #e5e7eb' }}>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
                      <Typography variant="body1" fontWeight={600}>Subtotal:</Typography>
                      <Typography variant="body1" fontWeight={600}>€{subTotal.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
                      <Typography variant="body1" fontWeight={600} color="text.secondary">Tax Amount:</Typography>
                      <Typography variant="body1" fontWeight={600} color="text.secondary">€{totalTax.toFixed(2)}</Typography>
                    </Box>
                    {discountAmount > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
                        <Typography variant="body1" fontWeight={600} color="error.main">Discount:</Typography>
                        <Typography variant="body1" fontWeight={600} color="error.main">-€{discountAmount.toFixed(2)}</Typography>
                      </Box>
                    )}
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, bgcolor: '#667eea', py: 2, borderRadius: 2, mt: 2 }}>
                      <Typography variant="h6" fontWeight={700} color="white">
                        Total Amount:
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="white">
                        €{totalAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

      {/* Notes Section */}
      {invoice.Notes && (
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mt: 3, border: '2px solid #e0e7ff' }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" mb={2}>
            📝 Notes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {invoice.Notes}
          </Typography>
        </Paper>
      )}
      </Box>
    </Container>
  );
}

export default PurchaseOrderDetailPage;